/**
 * Builds a step-by-step thinking tracker from SSE investigation progress events.
 * Steps adapt to whatever tools the agent actually calls for each question.
 */

import type { InvestigationProgressEvent } from '../../types/investigator';
import {
  formatArgsMeta,
  formatToolResultMeta,
  toolStepLabel,
} from './thinkingLabels';

export type ThinkingStepStatus = 'pending' | 'running' | 'done' | 'error';

export interface ThinkingStep {
  id: string;
  status: ThinkingStepStatus;
  label: string;
  /** Secondary detail under the title (entity match, claim counts, etc.). */
  meta?: string;
  /** Internal tool name — shown as a tag only while the step is active. */
  tool?: string;
}

export interface ZoeThinkingModel {
  steps: ThinkingStep[];
  headerLabel: string;
  warningNotice?: string;
  questionType?: string;
  /** True once the agent reports complete (progress snaps to 100%). */
  finished: boolean;
}

const UNDERSTAND_ID = 'understand';
const WINDOW_ID = 'window';
const COMPOSE_ID = 'compose';

function initialSteps(): ThinkingStep[] {
  return [
    { id: UNDERSTAND_ID, status: 'running', label: 'Understanding your question' },
    { id: COMPOSE_ID, status: 'pending', label: 'Compose answer' },
  ];
}

export function createInitialThinkingModel(): ZoeThinkingModel {
  return {
    steps: initialSteps(),
    headerLabel: 'Zoe is investigating…',
    finished: false,
  };
}

function upsertBeforeCompose(
  steps: ThinkingStep[],
  step: ThinkingStep,
): ThinkingStep[] {
  const without = steps.filter((s) => s.id !== step.id && s.id !== COMPOSE_ID);
  const compose =
    steps.find((s) => s.id === COMPOSE_ID) ??
    ({ id: COMPOSE_ID, status: 'pending', label: 'Compose answer' } as ThinkingStep);
  return [...without, step, compose];
}

function markRunningDone(steps: ThinkingStep[], exceptId?: string): ThinkingStep[] {
  return steps.map((s) =>
    s.status === 'running' && s.id !== exceptId && s.id !== COMPOSE_ID
      ? { ...s, status: 'done' as const, tool: undefined }
      : s,
  );
}

function activateCompose(steps: ThinkingStep[]): ThinkingStep[] {
  return steps.map((s) =>
    s.id === COMPOSE_ID
      ? { ...s, status: 'running' as const, tool: 'synthesise_response' }
      : s.status === 'running'
        ? { ...s, status: 'done' as const, tool: undefined }
        : s,
  );
}

function truncate(text: string, max = 90): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

/**
 * Apply one SSE progress event to the thinking model.
 * Pure function — safe to use inside React setState.
 */
export function applyThinkingProgress(
  model: ZoeThinkingModel,
  event: InvestigationProgressEvent,
): ZoeThinkingModel {
  // `narrating` may arrive after a *_complete event (route ordering); let it
  // through the finished-guard so it can still update the header.
  if (model.finished && event.type !== 'error' && event.type !== 'narrating') return model;

  switch (event.type) {
    case 'plan': {
      const entities = event.plan.entities_mentioned?.filter(Boolean) ?? [];
      const meta =
        entities.length > 0
          ? entities.slice(0, 3).join(' · ')
          : truncate(event.plan.question_understanding || event.plan.question_type);

      let steps = model.steps.map((s) =>
        s.id === UNDERSTAND_ID
          ? {
              ...s,
              status: 'done' as const,
              label: 'Understood the question',
              meta: meta || undefined,
              tool: undefined,
            }
          : s,
      );

      // Seed upcoming tools from the plan as pending (before compose).
      const proposed = event.plan.proposed_tools ?? [];
      for (const t of proposed.slice(0, 4)) {
        const id = `plan-${t.name}`;
        if (steps.some((s) => s.id === id || s.tool === t.name)) continue;
        steps = upsertBeforeCompose(steps, {
          id,
          status: 'pending',
          label: toolStepLabel(t.name),
          tool: t.name,
        });
      }

      return {
        ...model,
        steps,
        questionType: event.plan.question_type,
        headerLabel: 'Zoe is investigating…',
      };
    }

    case 'window_validated': {
      const from = event.window.from?.slice(0, 10) ?? '';
      const to = event.window.to?.slice(0, 10) ?? '';
      const sameDay = from && to && from === to;
      const label = sameDay
        ? `Resolved date to ${from}`
        : `Resolved window ${from || '…'} → ${to || '…'}`;
      const meta = event.adjusted
        ? event.adjustment_reason || 'Adjusted to available data window'
        : 'Using current date context';

      return {
        ...model,
        steps: upsertBeforeCompose(markRunningDone(model.steps), {
          id: WINDOW_ID,
          status: 'done',
          label,
          meta,
        }),
      };
    }

    case 'thinking': {
      // Soft status only — avoid noisy per-iteration rows.
      return {
        ...model,
        headerLabel: event.iteration > 1
          ? 'Digging deeper…'
          : 'Zoe is investigating…',
      };
    }

    case 'tool_call': {
      const tool = event.tool;
      const id = `tool-${event.iteration}-${tool}`;
      const label = toolStepLabel(tool);
      const meta = formatArgsMeta(event.args_preview);

      // Activate a matching pending plan seed if present.
      let steps = markRunningDone(model.steps);
      const planSeed = steps.find(
        (s) =>
          s.status === 'pending' &&
          s.id !== COMPOSE_ID &&
          (s.tool === tool || s.id === `plan-${tool}`),
      );

      if (planSeed) {
        steps = steps.map((s) =>
          s.id === planSeed.id
            ? {
                ...s,
                id, // re-key so tool_result can match iteration+tool
                status: 'running' as const,
                label,
                meta,
                tool,
              }
            : s,
        );
      } else {
        steps = upsertBeforeCompose(steps, {
          id,
          status: 'running',
          label,
          meta,
          tool,
        });
      }

      return { ...model, steps, headerLabel: 'Zoe is investigating…' };
    }

    case 'tool_result': {
      const tool = event.tool;
      const meta = formatToolResultMeta(event.claims, event.limitations, event.cached);
      const match =
        model.steps.find(
          (s) => s.status === 'running' && (s.tool === tool || s.id.endsWith(`-${tool}`)),
        ) ??
        [...model.steps].reverse().find((s) => s.status === 'running' && s.id !== COMPOSE_ID);

      if (!match) return model;

      return {
        ...model,
        steps: model.steps.map((s) =>
          s.id === match.id
            ? { ...s, status: 'done' as const, meta, tool: undefined }
            : s,
        ),
      };
    }

    case 'sub_agent': {
      const id = `sub-${event.agent}-${event.tool ?? event.status}`;
      if (event.status === 'starting' || event.status === 'running') {
        const label = event.message || 'Consulting Profit Agent';
        return {
          ...model,
          steps: upsertBeforeCompose(markRunningDone(model.steps), {
            id,
            status: 'running',
            label,
            tool: event.tool ? `profit_agent/${event.tool}` : 'invoke_profit_agent',
            meta: event.tool ? toolStepLabel(`profit_agent/${event.tool}`) : undefined,
          }),
          headerLabel: 'Checking profitability…',
        };
      }

      const status = event.status === 'error' ? 'error' : 'done';
      return {
        ...model,
        steps: model.steps.map((s) =>
          s.status === 'running' && (s.id.startsWith('sub-') || s.tool?.startsWith('profit_agent/'))
            ? {
                ...s,
                status,
                meta: event.message,
                tool: undefined,
              }
            : s,
        ),
      };
    }

    // ── bound_plan harness mode ──────────────────────────────────────────────
    case 'bound_plan_understanding': {
      const meta = truncate(event.question_understanding);
      return {
        ...model,
        steps: model.steps.map((s) =>
          s.id === UNDERSTAND_ID
            ? {
                ...s,
                status: 'done' as const,
                label: 'Understood the question',
                meta: meta || undefined,
                tool: undefined,
              }
            : s,
        ),
        headerLabel: 'Zoe is investigating…',
      };
    }

    case 'bound_plan_created': {
      let steps = model.steps;
      for (const s of event.steps.slice(0, 8)) {
        const id = `bp-${s.id}`;
        if (steps.some((st) => st.id === id)) continue;
        steps = upsertBeforeCompose(steps, {
          id,
          status: 'pending',
          label: toolStepLabel(s.tool),
          tool: s.tool,
        });
      }
      return { ...model, steps, headerLabel: 'Zoe is investigating…' };
    }

    case 'bound_plan_step_started': {
      // Bound steps run in parallel waves — several may be `running` at once,
      // so (unlike `tool_call`) do NOT mark sibling running steps done here.
      const id = `bp-${event.step}`;
      const label = toolStepLabel(event.tool);
      const meta = formatArgsMeta(event.args_preview);
      const exists = model.steps.some((s) => s.id === id);
      const steps = exists
        ? model.steps.map((s) =>
            s.id === id
              ? { ...s, status: 'running' as const, label, meta, tool: event.tool }
              : s,
          )
        : upsertBeforeCompose(model.steps, {
            id,
            status: 'running',
            label,
            meta,
            tool: event.tool,
          });
      return { ...model, steps, headerLabel: 'Zoe is investigating…' };
    }

    case 'bound_plan_step_finished': {
      // Match strictly by step id — concurrent completions are unambiguous.
      const id = `bp-${event.step}`;
      if (!model.steps.some((s) => s.id === id)) return model;
      const nextStatus: ThinkingStepStatus = event.status === 'error' ? 'error' : 'done';
      const meta =
        event.status === 'ok'
          ? formatToolResultMeta(event.claims ?? 0, event.limitations ?? 0, false)
          : event.status === 'error'
            ? event.error || 'Step failed'
            : event.skipped_reason || 'Skipped';
      return {
        ...model,
        steps: model.steps.map((s) =>
          s.id === id ? { ...s, status: nextStatus, meta, tool: undefined } : s,
        ),
      };
    }

    case 'bound_plan_complete': {
      let steps = activateCompose(model.steps);
      steps = steps.map((s) =>
        s.id === COMPOSE_ID || s.status === 'running'
          ? {
              ...s,
              status: 'done' as const,
              tool: undefined,
              label: s.id === COMPOSE_ID ? 'Answer ready' : s.label,
            }
          : s.status === 'pending'
            ? { ...s, status: 'done' as const }
            : s,
      );
      const nonAnswer = event.status && event.status !== 'answered';
      return {
        ...model,
        steps,
        finished: true,
        headerLabel: 'Investigation complete',
        warningNotice: nonAnswer
          ? model.warningNotice ?? `Answer status: ${event.status.replace(/_/g, ' ')}`
          : model.warningNotice,
      };
    }

    case 'narrating': {
      return { ...model, headerLabel: 'Writing the answer…' };
    }

    case 'reflection': {
      const gaps = event.result.gaps ?? [];
      const hasGaps = gaps.length > 0 || event.result.state === 'partial';
      if (!hasGaps) return model;

      return {
        ...model,
        headerLabel: 'Investigating with partial data…',
        warningNotice: gaps[0]
          ? truncate(gaps[0], 160)
          : 'Some evidence is incomplete — the answer may note reduced confidence.',
      };
    }

    case 'verification': {
      if (event.result.ok) return model;
      const issue = event.result.issues?.[0];
      return {
        ...model,
        warningNotice: issue
          ? truncate(issue, 160)
          : 'Verification found issues — answer may include caveats.',
      };
    }

    case 'complete': {
      let steps = activateCompose(model.steps);
      steps = steps.map((s) =>
        s.id === COMPOSE_ID || s.status === 'running'
          ? { ...s, status: 'done' as const, tool: undefined, label: s.id === COMPOSE_ID ? 'Answer ready' : s.label }
          : s.status === 'pending'
            ? { ...s, status: 'done' as const }
            : s,
      );
      return {
        ...model,
        steps,
        finished: true,
        headerLabel: 'Investigation complete',
      };
    }

    case 'error': {
      return {
        ...model,
        steps: model.steps.map((s) =>
          s.status === 'running'
            ? { ...s, status: 'error' as const, meta: event.message, tool: undefined }
            : s,
        ),
        headerLabel: 'Investigation interrupted',
        warningNotice: event.message,
      };
    }

    default:
      return model;
  }
}

/** Progress 0–100. Caps at 90 until finished, then snaps to 100. */
export function thinkingProgressPercent(model: ZoeThinkingModel, elapsedSec: number): number {
  if (model.finished) return 100;

  const actionable = model.steps.filter((s) => s.id !== COMPOSE_ID || s.status !== 'pending');
  const done = model.steps.filter((s) => s.status === 'done' || s.status === 'error').length;
  const total = Math.max(model.steps.length, 1);
  const stepPct = (done / total) * 90;

  // Soft time-based advance so the bar never feels stuck on long tool calls.
  const timePct = Math.min((elapsedSec / 8) * 40, 40);

  // Prefer whichever is further along, still capped at 90.
  const blend = Math.max(stepPct, Math.min(stepPct + timePct * 0.25, 90));
  void actionable;
  return Math.round(Math.min(90, Math.max(8, blend)));
}
